import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from 'react';

type Theme = 'light' | 'dark' | 'system';

interface ThemeColors {
  // Primary colors
  primary: string;
  primaryText: string;
  secondary: string;
  secondaryText: string;

  // Background colors
  background: string;
  backgroundSecondary: string;
  backgroundTertiary: string;

  // Text colors
  text: string;
  textSecondary: string;
  textTertiary: string;
  textInverse: string;

  // Border colors
  border: string;
  borderLight: string;

  // Status colors
  success: string;
  warning: string;
  danger: string;
  info: string;

  // Component specific
  card: string;
  modal: string;
  input: string;
  inputBorder: string;
  button: string;
  buttonText: string;
  buttonDisabled: string;

  // Navigation
  tabBar: string;
  tabBarText: string;
  tabBarTextActive: string;

  // Shadow
  shadow: string;
}

interface ThemeContextValue {
  theme: Theme;
  colors: ThemeColors;
  resolvedTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const lightColors: ThemeColors = {
  primary: '#3880ff',
  primaryText: '#ffffff',
  secondary: '#0cd1e8',
  secondaryText: '#ffffff',
  background: '#ffffff',
  backgroundSecondary: '#f7f7f7',
  backgroundTertiary: '#f0f0f0',
  text: '#000000',
  textSecondary: '#666666',
  textTertiary: '#999999',
  textInverse: '#ffffff',
  border: '#e0e0e0',
  borderLight: '#f0f0f0',
  success: '#2dd36f',
  warning: '#ffc409',
  danger: '#eb445a',
  info: '#3880ff',
  card: '#ffffff',
  modal: '#ffffff',
  input: '#ffffff',
  inputBorder: '#d1d5db',
  button: '#3880ff',
  buttonText: '#ffffff',
  buttonDisabled: '#9ca3af',
  tabBar: '#ffffff',
  tabBarText: '#666666',
  tabBarTextActive: '#3880ff',
  shadow: 'rgba(0, 0, 0, 0.1)',
};

const darkColors: ThemeColors = {
  primary: '#428cff',
  primaryText: '#ffffff',
  secondary: '#50c8ff',
  secondaryText: '#ffffff',
  background: '#1a1a1a',
  backgroundSecondary: '#2d2d2d',
  backgroundTertiary: '#404040',
  text: '#ffffff',
  textSecondary: '#b3b3b3',
  textTertiary: '#808080',
  textInverse: '#000000',
  border: '#404040',
  borderLight: '#2d2d2d',
  success: '#2dd36f',
  warning: '#ffd31a',
  danger: '#ff3b30',
  info: '#428cff',
  card: '#2d2d2d',
  modal: '#2d2d2d',
  input: '#2d2d2d',
  inputBorder: '#404040',
  button: '#428cff',
  buttonText: '#ffffff',
  buttonDisabled: '#666666',
  tabBar: '#1a1a1a',
  tabBarText: '#b3b3b3',
  tabBarTextActive: '#428cff',
  shadow: 'rgba(0, 0, 0, 0.3)',
};

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('theme') as Theme;
      if (saved && ['light', 'dark', 'system'].includes(saved)) {
        return saved;
      }
    }
    return 'system';
  });

  const resolvedTheme = useMemo(() => {
    if (theme === 'system') {
      if (typeof window !== 'undefined' && window.matchMedia) {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      }
      return 'light';
    }
    return theme;
  }, [theme]);

  const colors = useMemo(() => {
    return resolvedTheme === 'dark' ? darkColors : lightColors;
  }, [resolvedTheme]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    if (typeof window !== 'undefined') {
      localStorage.setItem('theme', newTheme);
    }
  };

  const toggleTheme = () => {
    if (theme === 'light') {
      setTheme('dark');
    } else if (theme === 'dark') {
      setTheme('light');
    } else {
      // If system, toggle to the opposite of current resolved
      setTheme(resolvedTheme === 'light' ? 'dark' : 'light');
    }
  };

  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

      const handleChange = () => {
        // Force re-render when system theme changes
        setThemeState(prev => prev);
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, []);

  // Apply CSS variables for global theming
  useEffect(() => {
    if (typeof document !== 'undefined') {
      const root = document.documentElement;
      Object.entries(colors).forEach(([key, value]) => {
        root.style.setProperty(`--color-${key}`, value);
      });

      // Set data-theme attribute for CSS targeting
      root.setAttribute('data-theme', resolvedTheme);
    }
  }, [colors, resolvedTheme]);

  const value = useMemo(
    () => ({
      theme,
      colors,
      resolvedTheme,
      setTheme,
      toggleTheme,
    }),
    [theme, colors, resolvedTheme],
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeContext;